/*
 * CORPORATE KIT PAGE (sections/corporate-kit.liquid).
 *
 * Prices, days and transit for the quantity and model on the page and the shop's country, the
 * total checkout will charge, the city picker, and the one cart line a kit is. The numbers are the cart function's own: the shop's price list
 * (cityframes.kit_prices, built by tools/kits from the corporate gift entries and proven against the
 * configurator) and this gift's entries; the arithmetic is assets/corporate-kit-price.js, shared with
 * the corporate collection.
 */
(() => {
  const root = document.querySelector('[data-corporate-kit]');
  const blob = document.querySelector('[data-kit-config]');
  if (!root || !blob) return;

  /* The sticky gallery's height, for where it holds (assets/corporate-kit.css, THE GALLERY STAYS IN VIEW). */
  const gallery = root.querySelector(':scope > .pdp-gallery');
  if (gallery && window.ResizeObserver) {
    new ResizeObserver(() => gallery.style.setProperty('--kit-gallery-height', `${gallery.offsetHeight}px`)).observe(gallery);
  }

  const data = JSON.parse(blob.textContent);
  const cfg = data.config;
  if (!cfg || !cfg.options) return;
  const t = data.t;

  /* The arithmetic is shared with the corporate collection: assets/corporate-kit-price.js. */
  const kp = window.CFKitPrice.create(cfg, data.country, { [data.kitId]: data.contents }, { ship: data.ship || {} });
  if (!kp.kit(data.kitId)) return;
  const { region, vat, rate, decimals, stepPrice, roundTo } = kp;
  /* What depends on VAT - net or with it, the import charges, the VAT rows - goes by the country's DE, EU or NONEU
     (the page's data-region); the freight by its zone (kp.region, since 2026-11-03), which the cart mark carries. */
  const taxRegion = root.dataset.region || kp.taxRegion;
  const money = (amount) => window.cfMoney(Math.round(amount * 100));

  /* English on the cart line, whatever the page's language: production and support read it. */
  const DESIGN_NAMES = { plaque: 'Plaque', bag: 'Carry bag', sleeve: 'Sleeve', box: 'Box', card: 'Card' };

  /* ---------------------------------------------------------------- state */

  /* The corporate collection links here with the quantity and model its band was set to
     (?qty=100&model=custom), so the kit opens as the buyer left it. Read here, never in Liquid:
     Shopify caches a page without regard to parameters it does not know. Without ?qty the quantity the
     collection last had in this browser (sections/main-collection-kits, cf-corporate-qty; Alex, 2026-10-30). */
  const params = new URLSearchParams(window.location.search);
  let collectionQty = 0;
  try { collectionQty = Number(window.localStorage.getItem('cf-corporate-qty')) || 0; } catch (error) { collectionQty = 0; }
  const startQty = Math.round(Number(params.get('qty')) || collectionQty);
  // The most pieces the PDP sells (the section's kit_max: 1,000; the price list and the configurator go further).
  const maxQty = Math.min(cfg.qty.max, Number(data.maxQty) || cfg.qty.max);
  const state = {
    // A gift sold as one model only has that one button (Liquid).
    model: (params.get('model') === 'custom' || !root.querySelector('[data-kit-model="city"]'))
      && root.querySelector('[data-kit-model="custom"]') ? 'custom' : 'city',
    city: '',
    qty: startQty >= cfg.qty.min ? Math.min(startQty, maxQty) : cfg.qty.min,
    // Where the kits go: the shop's country's freight zone - the settings window's (snippets/corporate-settings).
    region,
    // The design services chosen in "Your designs", by part ('bag', 'sleeve', 'box', 'card', 'plaque').
    services: [],
    // Each row's choice, 'own' or 'service', by part - none at first (Alex, 2026-10-30: "not preselected").
    designs: {},
    // Express delivery instead of Standard (Alex, 2026-10-31: "standard is default").
    express: false,
  };

  /* NET OR WITH VAT (2026-10-28): the price per piece, the pricing table, the freight and the custom
     model's line as the settings say (the page's data-prices, or what cf:corporate-prices says);
     the total always says both. A checked reverse-charge business, and outside the EU, net. */
  const verified = root.dataset.verified === 'true';
  const grossFor = (mode) => mode === 'gross' && vat > 0 && !verified && taxRegion !== 'NONEU';
  let showGross = grossFor(window.cfCorporatePrices || root.dataset.prices);
  const count = new Intl.NumberFormat(document.documentElement.lang || undefined);

  /* The kit for `qty` pieces: net per piece (the custom model spread over them, for reading),
     the net total, and the total with VAT exactly as the cart will build it - per piece with
     VAT on, times the pieces, plus the data work. */
  const quote = (qty = state.qty, model = state.model) =>
    kp.quote(data.kitId, { qty, model, region: state.region, services: state.services, express: state.express });

  /* Production and transit as the collection counts them too: assets/corporate-kit-price.js. */
  const readyDays = (k, qty, custom) => kp.readyDays(k, qty, custom, data.stock || {});

  /* STANDARD OR EXPRESS (Alex, 2026-10-31): the days on the way are the country's times from its tax entry
     (data.ship, sections/corporate-kit). Since the freight zones (2026-11-03) all four of them - Standard by parcel
     (DHL in Germany, else UPS Standard or Expedited) and by freight, Express by parcel (UPS Express) and by freight -
     for the way the pieces go (kp.transit); before them the UPS times, and the price list's region times for a
     pallet's Standard and for a country with no UPS Standard time. Express is offered where the entry has its
     time and the price list prices it. */
  const ship = data.ship || {};
  const span = (days) => (Array.isArray(days) && days.length ? days.join('–') : '');
  const offersExpress = () => Boolean(span(ship.express)) && kp.expressDelivery(state.region);
  const transitFor = (k, express) => {
    if (kp.zoned) return kp.transit(k, state.qty, state.region, express).replace('-', '–');
    if (express) return span(ship.express);
    const pallet = state.qty >= cfg.freight.palletFrom[k.size];
    return (!pallet && span(ship.standard)) || kp.transit(k, state.qty, state.region).replace('-', '–');
  };
  // "1 business day", "1–2 business days"
  const daysPhrase = (transit) => (transit === '1' ? t.transitOne : t.transitMany.replace('__T__', transit));

  /* WHEN THEY ARRIVE: the store's calendar (assets/cf-ship.js, window.CF_SHIP for the visitor's country). From today
     in Berlin, or the next workshop day; `days` to the ready-to-ship day - calendar days since the entries' own
     production days (2026-11-03), the next workshop day where that falls on a weekend or a day off; before, workshop
     days (Monday to Friday, not the workshop's days off) - then the transit's first and last day, counted on the
     days carriers deliver (Monday to Friday, not the country's public holidays). Written as the page's language
     writes a range ("Dec 10 – 14"). */
  const DAY = 86400000;
  const shipOff = new Set((window.CF_SHIP && window.CF_SHIP.workshop_off) || []);
  const destOff = new Set((window.CF_SHIP && window.CF_SHIP.dest_off) || []);
  const isoDay = (d) => d.toISOString().slice(0, 10);
  const weekday = (d) => d.getUTCDay() % 6 !== 0;
  const addDays = (d, n, off) => {
    let at = d;
    for (let left = n; left > 0;) {
      at = new Date(at.getTime() + DAY);
      if (weekday(at) && !off.has(isoDay(at))) left -= 1;
    }
    return at;
  };
  const dayFormat = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  function shipDates(days, transit, calendar = false) {
    let start = window.cfShip?.now?.().today;
    if (!start) { const now = new Date(); start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())); }
    while (!weekday(start) || shipOff.has(isoDay(start))) start = new Date(start.getTime() + DAY);
    let ships = calendar ? new Date(start.getTime() + days * DAY) : addDays(start, days, shipOff);
    while (calendar && (!weekday(ships) || shipOff.has(isoDay(ships)))) ships = new Date(ships.getTime() + DAY);
    const [first, last] = transit.split('–').map(Number);
    if (!first) return { ships: dayFormat.format(ships), arrives: '' };
    const from = addDays(ships, first, destOff);
    const to = addDays(ships, last || first, destOff);
    const range = from.getTime() === to.getTime() ? dayFormat.format(from)
      : dayFormat.formatRange ? dayFormat.formatRange(from, to) : `${dayFormat.format(from)} – ${dayFormat.format(to)}`;
    return { ships: dayFormat.format(ships), arrives: range };
  }

  /* ---------------------------------------------------------------- painting */

  const $ = (sel) => root.querySelector(sel);

  function paint() {
    const express = offersExpress();
    if (state.express && !express) {
      state.express = false;
      choose(root.querySelector('[data-kit-speed="standard"]'));
    }
    const q = quote();
    if (!q) return;
    const shown = (qq) => (showGross ? qq.totalGross : qq.totalNet);
    const withVat = showGross ? 1 + vat / 100 : 1;

    // The price table: a row per step of the size's price - its pieces, the price per piece and the total for those
    // pieces (no savings label: Alex, 2026-10-30) - the step the quantity is on tinted. A row is a button: it sets the
    // quantity to where its step starts.
    const steps = cfg.sizes[q.k.size].filter(([from]) => from <= maxQty);
    // Each figure keeps the room its bold takes (an invisible bold copy, assets/corporate-kit.css), so choosing
    // another row moves no column (Alex, 2026-10-30).
    const ladderCell = (text) => `<span class="corporate-kit__ladder-cell" data-text="${text}">${text}</span>`;
    const rows = steps.map(([from], i) => {
      const at = Math.max(from, cfg.qty.min);
      const next = steps[i + 1] ? steps[i + 1][0] : Infinity;
      const active = state.qty >= at && state.qty < next;
      const total = shown(quote(at));
      const each = total / at;
      return `<tr data-kit-ladder-qty="${at}" tabindex="0"${active ? ' class="is-active" aria-current="true"' : ''}><td>${ladderCell(count.format(at))}</td><td>${ladderCell(money(each))}</td><td>${ladderCell(money(total))}</td></tr>`;
    });
    $('[data-kit-ladder]').innerHTML = rows.join('');
    // Beside the quantity: the price per piece for exactly these pieces (Alex, 2026-10-30).
    $('[data-kit-each]').innerHTML = t.eachLine.replace('__P__', money(shown(q) / state.qty));

    // The custom model's terms, under the model buttons while it is chosen (sections/corporate-kit).
    const note = $('[data-kit-custom-note]');
    if (note) note.hidden = state.model !== 'custom';

    // Your designs: what each part's design service adds, once - the gift's fixed costs with it and without it.
    const plain = kp.kit(data.kitId);
    root.querySelectorAll('[data-kit-design]').forEach((row) => {
      const withService = kp.kit(data.kitId, [row.dataset.kitDesign]);
      row.hidden = !withService || !plain;
      if (row.hidden) return;
      const fee = money(roundTo((withService.f - plain.f) * rate * withVat, decimals));
      row.querySelector('[data-kit-design-fee]').textContent = t.designFee.replace('__P__', fee);
    });
    // The plaque's (its row of its own, THE PLAQUE): its service where the gift's entry offers one.
    const plaqueService = root.querySelector('[data-kit-plaque-choice="service"]');
    if (plaqueService) {
      const withService = kp.kit(data.kitId, ['plaque']);
      plaqueService.hidden = !withService || !plain;
      if (!plaqueService.hidden) {
        const fee = money(roundTo((withService.f - plain.f) * rate * withVat, decimals));
        plaqueService.querySelector('[data-kit-design-fee]').textContent = t.designFee.replace('__P__', fee);
      }
    }

    // Delivery (mockup A, 2026-10-30): Standard or Express with their days and their delivery per piece (2026-10-31);
    // when the gifts arrive, as dates from the store's calendar, the ready-to-ship day and the days on the way under
    // it; then the freight in the price, per piece and for all the pieces - each for the delivery chosen.
    const speeds = $('[data-kit-speeds]');
    speeds.hidden = !express;
    if (express) {
      speeds.querySelectorAll('[data-kit-speed]').forEach((tile) => {
        const fast = tile.dataset.kitSpeed === 'express';
        const each = (kp.freight(q.k, state.qty, state.region, fast) * rate * withVat) / state.qty;
        tile.querySelector('[data-kit-speed-days]').textContent = daysPhrase(transitFor(q.k, fast));
        tile.querySelector('[data-kit-speed-price]').textContent = t.speedPrice.replace('__P__', money(roundTo(each, decimals)));
      });
    }
    $('[data-kit-ship-title]').textContent = !express ? t.shipTitle : state.express ? t.shipExpress : t.shipStandard;
    $('[data-kit-sum-delivery-label]').textContent = !express ? t.sumDelivery : state.express ? t.shipExpress : t.shipStandard;
    const transit = transitFor(q.k, state.express);
    const freight = kp.freight(q.k, state.qty, state.region, state.express) * rate * withVat;
    const ready = readyDays(q.k, state.qty, q.custom);
    const when = shipDates(ready.days, transit, ready.calendar);
    $('[data-kit-arrives]').textContent = t.arrives.replace('__R__', when.arrives || when.ships);
    $('[data-kit-ready]').textContent = (transit ? t.shipReady.replace('__T__', daysPhrase(transit)) : t.shipReadyOnly)
      .replace('__S__', when.ships).replace('__D__', ready.days);
    $('[data-kit-ship-cost]').textContent = t.shipCost.replace('__P__', money(roundTo(freight / state.qty, decimals)))
      .replace('__A__', money(roundTo(freight, decimals))).replace('__C__', count.format(state.qty));
    // Delivery's window: production from the day the preview is approved (a custom model's 3D work before it), then
    // the days on the way.
    const modelDays = q.custom ? Number(q.k.dataDays) || 0 : 0;
    $('[data-kit-delivery-info]').textContent = [
      modelDays ? t.deliveryModel.replace('__M__', modelDays) : '',
      t.deliveryWhen.replace('__D__', ready.days - modelDays).replace('__C__', count.format(state.qty)).replace('__T__', daysPhrase(transit)),
    ].filter(Boolean).join(' ');
    // Import charges: outside the EU, where the country charges any (sections/corporate-kit, IMPORT CHARGES).
    const duties = $('[data-kit-duties]');
    if (duties) duties.hidden = taxRegion !== 'NONEU';

    // Good to know's Production and Delivery (sections/good-to-know, [kit_production] and [kit_delivery]): the same
    // days for the same pieces, by parcel or, from the zone's freight quantity for the cover and speed, by freight.
    const pallet = kp.byFreight(q.k, state.qty, state.region, state.express);
    // How they travel, in Delivery's window: parcels up to the freight quantity and freight from it, or parcels only.
    const packed = $('[data-kit-delivery-packed]');
    if (packed) {
      // Always on a pallet where no quantity the page sells goes by parcel: a magnetic box, or a row whose "freight
      // from" is at or below the minimum (glass to Japan from 10).
      const from = kp.freightFrom(q.k, state.region, state.express);
      packed.textContent = from == null ? t.packedParcels
        : from <= cfg.qty.min ? t.packedPallet
        : t.packedBoth.replace('__P__', count.format(from - 1)).replace('__F__', count.format(from));
    }
    // Import charges on UPS parcels can be paid through the tracking link: parcels only (Alex, 2026-11-02).
    const ups = $('[data-kit-import-ups]');
    if (ups) ups.hidden = pallet;
    document.querySelectorAll('[data-kit-fact="production"]').forEach((el) => {
      el.textContent = t.factProduction.replace('__D__', ready.days).replace('__C__', count.format(state.qty));
    });
    document.querySelectorAll('[data-kit-fact="delivery"]').forEach((el) => {
      el.textContent = transit ? (pallet ? t.factPallet : t.factParcel).replace('__T__', daysPhrase(transit)) : '';
    });

    // THE TOTAL: the pieces and a custom model net, the net total, the VAT checkout adds (0% for a
    // checked reverse-charge business, none outside the EU), and what is to pay - the cart's own sums.
    $('[data-kit-sum-items]').textContent = t.sumItems.replace('__C__', count.format(state.qty));
    // The design services on a line of their own (Alex, 2026-10-29): what they add as the cart prices them - the
    // pieces with them, less the pieces without - so the lines add up to the net total to the cent.
    const bare = state.services.length ? kp.quote(data.kitId, { qty: state.qty, model: state.model, region: state.region, express: state.express }) : q;
    // The pieces without their freight, and the freight on its own line: together the pieces as priced.
    const freightNet = roundTo(kp.freight(q.k, state.qty, state.region, state.express) * rate, decimals);
    $('[data-kit-sum-items-amount]').textContent = money(roundTo(bare.eachNet * state.qty, decimals) - freightNet);
    $('[data-kit-sum-delivery]').textContent = money(freightNet);
    $('[data-kit-sum-custom]').hidden = !q.custom;
    $('[data-kit-sum-custom-amount]').textContent = money(q.customNet);
    $('[data-kit-sum-services]').hidden = !state.services.length;
    $('[data-kit-sum-services-label]').textContent = state.services.length > 1 ? t.sumServiceMany : t.sumServiceOne;
    $('[data-kit-sum-services-amount]').textContent = money(roundTo(q.totalNet - bare.totalNet, decimals));
    const noVat = taxRegion === 'NONEU' || !vat;
    $('[data-kit-sum-net-row]').hidden = noVat;
    $('[data-kit-sum-vat-row]').hidden = noVat;
    $('[data-kit-sum-net]').textContent = money(q.totalNet);
    $('[data-kit-sum-vat-label]').textContent = verified ? t.sumReverse : t.sumVat.replace('__R__', count.format(vat));
    $('[data-kit-sum-vat]').textContent = money(verified ? 0 : q.totalGross - q.totalNet);
    $('[data-kit-sum-pay]').textContent = money(verified || noVat ? q.totalNet : q.totalGross);
  }

  /* ---------------------------------------------------------------- inputs */

  /* The tab buttons: one outlined per row, as on the CUBE page. */
  const choose = (option) => {
    [...option.parentElement.children].forEach((o) => {
      o.classList.toggle('active', o === option);
      o.querySelector('button')?.setAttribute('aria-pressed', String(o === option));
    });
  };

  /* THE STAND-IN (Alex, 2026-10-31): the pictures show New York. On an Available City the label on every picture of
     the model says so (sections/corporate-kit, snippets/kit-gallery-slides). */
  const paintExample = () => {
    root.querySelectorAll('[data-kit-example]').forEach((label) => { label.hidden = state.model !== 'city'; });
  };

  const showModel = () => {
    const cityRow = $('[data-kit-city-row]');
    if (cityRow) cityRow.hidden = state.model !== 'city';
    paintExample();
    // "Learn more" opens the chosen model's window.
    root.querySelectorAll('[data-kit-model-info]').forEach((button) => { button.hidden = button.dataset.kitModelInfo !== state.model; });
  };

  root.addEventListener('click', (event) => {
    const option = event.target.closest('[data-kit-model]');
    if (!option || !event.target.closest('button')) return;
    choose(option);
    state.model = option.dataset.kitModel;
    showModel();
    showError('');
    paint();
  });

  /* Standard or Express, in Delivery. */
  root.addEventListener('click', (event) => {
    const option = event.target.closest('[data-kit-speed]');
    if (!option || !event.target.closest('button')) return;
    choose(option);
    state.express = option.dataset.kitSpeed === 'express';
    paint();
  });

  /* Your designs: the buyer's own file or our design service, per part (sections/corporate-kit, YOUR DESIGNS). */
  root.addEventListener('click', (event) => {
    const option = event.target.closest('[data-kit-design-choice]');
    if (!option || !event.target.closest('button')) return;
    choose(option);
    const part = option.closest('[data-kit-design]').dataset.kitDesign;
    state.designs[part] = option.dataset.kitDesignChoice;
    const others = state.services.filter((s) => s !== part);
    state.services = option.dataset.kitDesignChoice === 'service' ? [...others, part].sort() : others;
    if (!unchosenDesign()) showError('');
    paint();
  });

  document.addEventListener('cf:corporate-prices', (event) => {
    showGross = grossFor(event.detail && event.detail.mode);
    paint();
  });

  const qtyInput = $('[data-kit-qty]');
  const setQty = (n) => {
    const qty = Math.min(maxQty, Math.max(cfg.qty.min, Math.round(Number(n) || cfg.qty.min)));
    state.qty = qty;
    qtyInput.value = qty;
    paint();
  };
  qtyInput.addEventListener('change', () => setQty(qtyInput.value));
  qtyInput.addEventListener('input', () => {
    const n = Number(qtyInput.value);
    if (n >= cfg.qty.min && n <= maxQty) { state.qty = Math.round(n); paint(); }
  });
  root.querySelectorAll('[data-kit-step]').forEach((button) => {
    button.addEventListener('click', () => setQty(state.qty + Number(button.dataset.kitStep)));
  });

  const ladder = $('[data-kit-ladder]');
  ladder.addEventListener('click', (event) => {
    const row = event.target.closest('[data-kit-ladder-qty]');
    if (row) setQty(row.dataset.kitLadderQty);
  });
  ladder.addEventListener('keydown', (event) => {
    const row = event.target.closest('[data-kit-ladder-qty]');
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    setQty(row.dataset.kitLadderQty);
    ladder.querySelector(`[data-kit-ladder-qty="${row.dataset.kitLadderQty}"]`)?.focus();
  });

  /* The city, through the same picker the CUBE Set uses - one slot, so a second pick replaces
     the first. Every city is offered: kits are made to order, so none is out of stock. */
  // The gallery keeps the gift's own pictures whatever city is picked (Alex, 2026-10-30: "when we select an available
  // city, don't bring the image of that city into the gallery"); the city goes to the plaque and the cart line.
  const picker = $('[data-kit-picker]');
  const variantOf = (id) => (window.CF_CUBE_VARIANTS || []).find((v) => String(v.id) === String(id));
  const cityChanged = () => {
    const v = variantOf(state.city);
    root.dispatchEvent(new CustomEvent('kit:city', { detail: { id: state.city, name: v ? v.title.split(' - ')[0] : '' } }));
    if (state.city) showError('');
    paint();
  };
  if (picker && typeof picker.configure === 'function' && window.CFCities) {
    picker.configure({
      slots: () => 1,
      getSelection: () => [state.city || ''],
      setSelection: (next) => { state.city = next[0] || ''; },
      variants: () => window.CF_CUBE_VARIANTS || [],
      designs: () => [],
      label: (v) => window.CFCities.label(v, t.picker),
      optionLines: (v) => [window.CFCities.label({ ...v, variantCity: '' }, t.picker), v.variantCity || ''],
      filters: { bestseller: (v) => v.isFavorite === true, new: (v) => v.isNew === true },
      outOfStock: () => false,
      t: t.picker,
      onChange: cityChanged,
    });
  }

  /* ---------------------------------------------------------------- the plaque */

  /* THE PLAQUE (sections/corporate-kit, Your designs; Alex, 2026-11-03): the other rows' two choices. "I'll send my
     file" opens the plaque's window (snippets/kit-plaque-window), where the buyer writes the text now, uploads a design
     now or sends the file after the order (a pill switch: Text, Upload, Later) - chosen once the window is saved, and
     shown under the row with Change. "Design service" is the plaque's service, |svc:plaque. state.plaque: null, 'custom' (written or uploaded, as
     state.plaqueMode 'text' / 'design'), 'later' (the buyer's file after the order) or 'service'. */
  const plaqueOption = root.querySelector('[data-kit-plaque]');
  const plaqueWindow = document.getElementById('kit-plaque-window');
  const headlineInput = () => document.getElementById('plaque-note-1');
  const subInput = () => document.getElementById('plaque-subnote-1');
  const plaqueMaterial = plaqueOption?.dataset.material === 'brass' ? 'brass' : 'steel';
  state.plaque = null;
  state.plaqueMode = 'text';
  state.design = null;

  // Emoji are not engraved; hearts are (as ♡), as on the product pages (assets/cf-pdp.js, validatePlaqueInput).
  const cleanPlaque = (value) => String(value || '')
    .replace(/❤️/g, '\u0001').replace(/❤/g, '\u0002').replace(/♥/g, '\u0003')
    .replace(/[\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}|]/gu, '')
    .replace(/\u0001/g, '❤️').replace(/\u0002/g, '❤').replace(/\u0003/g, '♥');
  const words = () => [cleanPlaque(headlineInput()?.value).trim(), cleanPlaque(subInput()?.value).trim()];

  // A plate (snippets/plaque-plate): the words in capitals, a heart drawn as ♡, a line wider than the plate drawn
  // smaller - as the product pages' plaque designer draws it (assets/cf-plaque-designer.js, paintPlate).
  function paintPlate(plate, l1, l2) {
    if (!plate) return;
    plate.dataset.material = plaqueMaterial;
    const text = plate.querySelector('[data-plate-text]');
    plate.querySelector('[data-plate-l1]').textContent = l1.toUpperCase().replace(/❤️|❤|♥/g, '♡');
    plate.querySelector('[data-plate-l2]').textContent = l2.toUpperCase().replace(/❤️|❤|♥/g, '♡');
    text.style.removeProperty('--plate-fit');
    requestAnimationFrame(() => {
      const room = plate.clientWidth * 0.92;
      const widest = Math.max(...Array.from(text.children).map((span) => span.scrollWidth));
      if (room && widest > room) text.style.setProperty('--plate-fit', (room / widest).toFixed(3));
    });
  }

  // A plate with the uploaded design on it instead of the words.
  function paintDesign(box, design) {
    if (!box) return;
    const img = box.querySelector('[data-kit-plaque-design]');
    img.hidden = !design;
    if (design) img.src = design.preview;
    else img.removeAttribute('src');
    paintPlate(box.querySelector('[data-plaque-plate]'), '', '');
  }

  /* ---- the window: the pill switch's three tabs (x-tabs) and their panels, text / upload / later */
  const tabs = plaqueWindow?.querySelector('[data-kit-plaque-tabs]');
  const ways = plaqueWindow ? [...plaqueWindow.querySelectorAll('[data-kit-plaque-way]')] : [];
  const panels = tabs ? [...tabs.querySelectorAll(':scope > [role="tabpanel"]')] : [];
  const uploadInput = plaqueWindow?.querySelector('[data-kit-plaque-file]');
  const uploadZone = plaqueWindow?.querySelector('.plaque-upload__zone');
  const uploadCta = plaqueWindow?.querySelector('[data-kit-plaque-cta]');
  const uploadStatus = plaqueWindow?.querySelector('[data-kit-plaque-status]');
  const textError = plaqueWindow?.querySelector('[data-kit-plaque-error]');
  let way = 'text';
  let saved = false;
  let copy = null;

  // The switch on the way chosen (selectedIndex: the window moves itself to the end of the page as it opens, and the
  // tabs' own click listener stays where they were - as the product pages' ideas tabs, assets/cf-plaque-designer.js).
  function paintWindow() {
    const index = ways.findIndex((tab) => tab.dataset.kitPlaqueWay === way);
    if (tabs && index > -1 && tabs.selectedIndex !== index) tabs.selectedIndex = index;
    const [head, sub] = words();
    paintPlate(panels[0]?.querySelector('[data-plaque-plate]'), head, sub);
    panels[0]?.querySelectorAll('[data-kit-plaque-count]').forEach((count, i) => {
      const input = i === 0 ? headlineInput() : subInput();
      count.textContent = `${input.value.length} / ${input.maxLength}`;
    });
    paintDesign(panels[1]?.querySelector('.kit-plaque__plate'), state.design);
  }

  /* ONE HEIGHT FOR THE THREE (Alex, 2026-11-03: "keep its height when we switch between the 3 options"): every panel as
     tall as the tallest, so the window neither grows nor shrinks as the switch changes (and the tabs' own height
     animation has nothing to do). Measured as the window opens, all three shown for that one measurement before it is
     drawn; again when a panel gains a line (an error, the upload's status), only ever taller while the window is open. */
  let panelHeight = 0;
  function evenPanels(fresh = false) {
    if (!tabs || !panels.length) return;
    if (fresh) panelHeight = 0;
    tabs.style.removeProperty('--kit-plaque-panel');
    const was = panels.map((panel) => panel.hidden);
    panels.forEach((panel) => { panel.hidden = false; });
    const tallest = Math.ceil(Math.max(...panels.map((panel) => panel.getBoundingClientRect().height)));
    panels.forEach((panel, i) => { panel.hidden = was[i]; });
    panelHeight = Math.max(panelHeight, tallest);
    if (panelHeight > 0) tabs.style.setProperty('--kit-plaque-panel', `${panelHeight}px`);
  }

  function setUploadStatus(text, isError = false) {
    if (!uploadStatus) return;
    uploadStatus.textContent = text || '';
    uploadStatus.classList.toggle('text-error', isError);
    if (plaqueWindow.open) evenPanels();
  }

  /* An own design: a PNG / JPG of the whole plaque, sent to the API (printable-city-models,
     pipeline/plaque_upload.py), which redraws it into the one-tone 600 dpi file production
     engraves and a grey preview, shown on the plate. The file never touches this page's servers;
     what comes back is two links. */
  async function uploadDesign(file) {
    if (!file) return;
    const isImage = /^image\/(png|jpeg)$/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
    if (!isImage) { setUploadStatus(t.uploadErrors.type, true); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadStatus(t.uploadErrors.size, true); return; }
    setUploadStatus(t.uploading);
    try {
      const res = await fetch(`${t.plaqueUploadUrl}?name=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'image/png' },
        body: file,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.id) throw new Error(body.error || 'generic');
      state.design = body;
      setUploadStatus(t.uploaded.replace('__N__', body.name));
      if (uploadCta) uploadCta.textContent = t.replace;
    } catch (error) {
      setUploadStatus(t.uploadErrors[error.message] || t.uploadErrors.generic, true);
    }
    paintWindow();
  }

  /* ---- the row */
  function paintPlaqueRow() {
    if (!plaqueOption) return;
    const chosenAs = state.plaque === 'service' ? 'service' : state.plaque ? 'own' : null;
    plaqueOption.querySelectorAll('[data-kit-plaque-choice]').forEach((option) => {
      const on = option.dataset.kitPlaqueChoice === chosenAs;
      option.classList.toggle('active', on);
      option.querySelector('button').setAttribute('aria-pressed', String(on));
    });
    const chosen = plaqueOption.querySelector('[data-kit-plaque-chosen]');
    const own = state.plaque === 'custom' || state.plaque === 'later';
    chosen.hidden = !own;
    if (!own) return;
    const media = chosen.querySelector('[data-kit-plaque-chosen-media]');
    const l1 = chosen.querySelector('[data-kit-plaque-chosen-l1]');
    const l2 = chosen.querySelector('[data-kit-plaque-chosen-l2]');
    chosen.classList.toggle('kit-plaque__chosen--words', state.plaque === 'later');
    media.hidden = state.plaque === 'later';
    if (state.plaque === 'later') {
      l1.textContent = t.plaqueLater;
      l2.textContent = t.plaqueLaterLine;
    } else if (state.plaqueMode === 'design') {
      paintDesign(media, state.design);
      l1.textContent = t.ownDesign.replace('__N__', state.design ? state.design.name : '');
      l2.textContent = '';
    } else {
      const [head, sub] = words();
      paintDesign(media, null);
      paintPlate(media.querySelector('[data-plaque-plate]'), head, sub);
      l1.textContent = head;
      l2.textContent = sub;
    }
  }

  function setPlaque(choice) {
    state.plaque = choice;
    // The design service is the plaque's only while it is chosen.
    state.services = state.services.filter((s) => s !== 'plaque');
    if (choice === 'service') state.services = [...state.services, 'plaque'].sort();
    if (choice) showError('');
    paintPlaqueRow();
    paint();
  }

  if (plaqueOption && plaqueWindow) {
    plaqueOption.querySelector('[data-kit-plaque-choice="service"] button').addEventListener('click', () => setPlaque('service'));

    // Opening keeps a copy of what is there; closing any other way than saving puts it back.
    plaqueWindow.addEventListener('dialog:before-show', () => {
      saved = false;
      copy = { words: [headlineInput().value, subInput().value], design: state.design };
      way = state.plaque === 'later' ? 'later' : state.plaque === 'custom' && state.plaqueMode === 'design' ? 'upload' : 'text';
      textError.hidden = true;
      setUploadStatus(state.design ? t.uploaded.replace('__N__', state.design.name) : '');
      if (uploadCta) uploadCta.textContent = state.design ? t.replace : t.uploadCta;
      paintWindow();
      evenPanels(true);
    });
    // A new width can wrap a panel's lines differently: measured again.
    window.addEventListener('resize', () => { if (plaqueWindow.open) evenPanels(true); });
    plaqueWindow.addEventListener('dialog:after-hide', () => {
      if (saved || !copy) return;
      [headlineInput().value, subInput().value] = copy.words;
      state.design = copy.design;
    });

    ways.forEach((tab) => tab.addEventListener('click', () => {
      way = tab.dataset.kitPlaqueWay;
      paintWindow();
    }));
    [headlineInput(), subInput()].forEach((input) => input.addEventListener('input', () => {
      const cleaned = cleanPlaque(input.value);
      if (cleaned !== input.value) input.value = cleaned;
      if (headlineInput().value.trim()) textError.hidden = true;
      paintWindow();
    }));
    uploadInput?.addEventListener('change', () => {
      uploadDesign(uploadInput.files && uploadInput.files[0]);
      uploadInput.value = '';
    });
    if (uploadZone) {
      ['dragenter', 'dragover'].forEach((type) => uploadZone.addEventListener(type, (event) => {
        event.preventDefault();
        uploadZone.classList.add('is-dragover');
      }));
      ['dragleave', 'drop'].forEach((type) => uploadZone.addEventListener(type, () => uploadZone.classList.remove('is-dragover')));
      uploadZone.addEventListener('drop', (event) => {
        event.preventDefault();
        uploadDesign(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]);
      });
    }

    // The tick and the black button save: the way that is open, with what it needs - a headline, an uploaded design.
    const save = () => {
      if (way === 'text' && !words()[0]) {
        textError.hidden = false;
        evenPanels();
        headlineInput().focus();
        return;
      }
      if (way === 'upload' && !state.design) {
        setUploadStatus(t.designRequired, true);
        return;
      }
      if (way === 'later') setPlaque('later');
      else {
        state.plaqueMode = way === 'upload' ? 'design' : 'text';
        setPlaque('custom');
      }
      saved = true;
      plaqueWindow.hide();
    };
    plaqueWindow.querySelector('[data-kit-plaque-save]').addEventListener('click', save);
    plaqueWindow.querySelector('[data-kit-plaque-save-row] .button').addEventListener('click', save);
  }

  /* ---------------------------------------------------------------- the cart */

  // The first design row on show with nothing chosen (a row the gift hides, or the plaque's while it is not "Provide
  // later", asks for nothing).
  function unchosenDesign() {
    return [...root.querySelectorAll('[data-kit-design]')].find((row) => row.offsetParent && !state.designs[row.dataset.kitDesign]);
  }

  function showError(text) {
    const errorEl = $('[data-kit-error]');
    errorEl.textContent = text;
    errorEl.hidden = !text;
  }

  $('[name="corporate-kit-add"]').addEventListener('click', async (event) => {
    const button = event.currentTarget;
    showError('');
    if (state.model === 'city' && !state.city) {
      showError(t.chooseCity);
      picker?.querySelector('.bcv2-ms-trigger')?.click();
      return;
    }
    if (plaqueOption && !state.plaque) {
      showError(t.plaqueRequired);
      plaqueOption.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const unchosen = unchosenDesign();
    if (unchosen) {
      showError(t.designsRequired);
      unchosen.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // <pieces>|city:<id> or custom, then |<region>, |express, |svc:... - see MARK in the cart function (bundle-price,
    // src/kits.js), which prices it and holds checkout to the region.
    const mark = `${state.qty}|${state.model === 'custom' ? 'custom' : `city:${state.city}`}|${state.region}`
      + (state.express ? '|express' : '')
      + (state.services.length ? `|svc:${state.services.join('+')}` : '');
    const properties = {
      _kit: mark,
      Pieces: String(state.qty),
      // The country the gift is priced for (Alex, 2026-11-02; it said the region, "Outside the EU"); the region
      // that prices it and holds checkout to it is in the mark.
      Delivery: (t.deliveryCountry || state.region) + (state.express ? ', Express' : ''),
    };
    if (state.model === 'custom') properties.Model = t.customModel;
    else properties.City = (variantOf(state.city) || {}).title || state.city;
    if (state.plaque === 'later' || state.plaque === 'service') {
      // Designed after the order: the buyer's file, or our design service (|svc:plaque, "Design service").
      properties.Plaque = 'Provided later';
    } else if (state.plaque === 'custom' && state.plaqueMode === 'design') {
      // The redrawn file, linked on the order so the workshop opens it from there; the id for later
      // tooling (the corporate order record).
      properties['Plaque design'] = state.design.file;
      properties._plaque_design = state.design.id;
    } else if (state.plaque === 'custom') {
      const [headline, subHeadline] = words();
      if (headline) properties.Headline = headline;
      if (subHeadline) properties['Sub-Headline'] = subHeadline;
    }
    // The design services, in English like every visible property ("Carry bag, Card"); the files come after the order.
    if (state.services.length) properties['Design service'] = state.services.map((part) => DESIGN_NAMES[part] || part).join(', ');
    // The colours picked in "Your colour", one per part ("Frame colour": "#C8102E").
    Object.assign(properties, window.CFKitColour?.properties() || {});

    button.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(root.dataset.kitId), quantity: 1, properties }] }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || res.statusText);
      document.dispatchEvent(new Event('cart:refresh'));
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show();
      else window.location.href = '/cart';
    } catch (error) {
      console.error('Corporate kit: add to cart failed', error);
      showError(t.error);
    } finally {
      button.disabled = false;
    }
  });

  /* ---------------------------------------------------------------- the country */

  /* A NEW COUNTRY in the settings window (assets/corporate-settings.js; "Change delivery country" in Delivery opens
     it): that country's shop loads this page again, so what was
     chosen here goes into the address first and is chosen again there. A plaque design file does not travel - the
     plaque is asked for again. */
  const CARRY = ['city', 'svc', 'own', 'plaque', 'p1', 'p2', 'colour', 'delivery'];
  const carried = () => {
    const typed = state.plaque === 'custom' && state.plaqueMode === 'text';
    return {
      qty: state.qty,
      model: state.model === 'custom' ? 'custom' : '',
      city: state.model === 'city' ? state.city : '',
      svc: state.services.join(','),
      own: Object.keys(state.designs).filter((part) => state.designs[part] === 'own').join(','),
      plaque: state.plaque === 'later' || state.plaque === 'service' || typed ? state.plaque : '',
      p1: typed ? words()[0] : '',
      p2: typed ? words()[1] : '',
      colour: window.CFKitColour?.carry() || '',
      delivery: state.express ? 'express' : '',
    };
  };
  // Into the address before the page is left: for another country (assets/corporate-settings.js), and for signing in
  // or the reload after a VAT number check (assets/b2b-vat.js).
  const keepState = () => {
    const url = new URL(window.location.href);
    Object.entries(carried()).forEach(([key, value]) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key)));
    window.history.replaceState(window.history.state, '', url);
  };
  document.addEventListener('cf:corporate-country', keepState);
  document.addEventListener('cf:keep-state', keepState);

  // THE SAME GIFT AS A CUSTOM MODEL (sections/corporate-kit, the link under the city picker; Alex, 2026-11-02): it opens
  // with what was chosen here, but the city, which a custom model has none of.
  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-kit-other]');
    if (!link) return;
    const url = new URL(link.getAttribute('href'), window.location.href);
    Object.entries(carried()).forEach(([key, value]) => { if (value && key !== 'city' && key !== 'model') url.searchParams.set(key, value); });
    link.href = url.toString();
  });

  // Back from another country: the choices, once every script on the page is there (the colour step listens for the
  // city), then out of the address again.
  const restore = () => {
    const city = params.get('city');
    if (state.model === 'city' && city && variantOf(city)) {
      state.city = city;
      picker?.refresh?.();
      cityChanged();
    }
    const carriedPlaque = plaqueOption && plaqueWindow ? params.get('plaque') : '';
    if (carriedPlaque === 'custom' && params.get('p1')) {
      headlineInput().value = params.get('p1');
      subInput().value = params.get('p2') || '';
      state.plaqueMode = 'text';
      setPlaque('custom');
    } else if (carriedPlaque === 'later' || carriedPlaque === 'service') setPlaque(carriedPlaque);
    ['svc', 'own'].forEach((key) => (params.get(key) || '').split(',').filter(Boolean).forEach((part) => {
      const choice = key === 'svc' ? 'service' : 'own';
      root.querySelector(`[data-kit-design="${part}"]:not([hidden]) [data-kit-design-choice="${choice}"] button`)?.click();
    }));
    if (params.get('colour')) window.CFKitColour?.restore(params.get('colour'));
    // Express where the new country offers it too.
    if (params.get('delivery') === 'express' && offersExpress()) root.querySelector('[data-kit-speed="express"] button')?.click();
    if (CARRY.some((key) => params.has(key))) {
      const url = new URL(window.location.href);
      CARRY.forEach((key) => url.searchParams.delete(key));
      window.history.replaceState(window.history.state, '', url);
    }
  };

  if (state.qty !== cfg.qty.min) qtyInput.value = state.qty;
  if (state.model === 'custom') choose(root.querySelector('[data-kit-model="custom"]'));
  showModel();
  paint();
  // A deferred script runs before DOMContentLoaded but after the page is parsed ("interactive"): the event, not the
  // state, says the colour step's script has run too.
  if (document.readyState === 'complete') restore();
  else document.addEventListener('DOMContentLoaded', restore, { once: true });
})();
